/**
 * RigDescriptor — the plain-data description of any concrete skeleton entering
 * the kernel (ADR-003 §2b). Adapters construct these; the kernel never sees an
 * engine object. World bind comes from inverse-bind matrices — the ONLY
 * sanctioned bind source (v2's costliest lesson).
 */
import { z } from 'zod';
import { SchemaValidationError, UnmappedBoneError } from './errors.js';
import { vec3Schema, type Ontology } from './ontology.js';

export const quatDataSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
  w: z.number(),
});

export const transformSchema = z.object({
  position: vec3Schema,
  rotation: quatDataSchema,
  scale: vec3Schema,
});

export const upAxisSchema = z.enum(['Y', 'Z']);
export const facingSchema = z.enum(['+X', '-X', '+Z', '-Z']);

/** Where a mapping entry came from — reviewable provenance (ADR-004 §2). */
export const mappingProvenanceSchema = z.union([
  z.literal('auto'),
  z.literal('manual'),
  /** The bone itself was fabricated at import (tip synthesis, ADR-010 §12). */
  z.literal('synthesized'),
  /**
   * The bone was CONSTRUCTED by the Hand Studio graft (ADR-010 §11): a
   * fingerless generated rig's finger bones fabricated from voxel evidence
   * (place-digits + author-frames → graft-digits). Distinct from tip
   * 'synthesized' — a graft builds whole articulating chains, not a leaf.
   */
  z.literal('grafted'),
  z.string().regex(/^prior:[a-z0-9-]+$/),
]);

export const mappingEntrySchema = z.object({
  /** The rig's own bone name. */
  rigBone: z.string().min(1),
  /** Canonical ontology ID it maps to. */
  ontologyId: z.string().min(1),
  confidence: z.number().min(0).max(1),
  provenance: mappingProvenanceSchema,
});
export type MappingEntry = z.infer<typeof mappingEntrySchema>;

export const rigBoneSchema = z.object({
  name: z.string().min(1),
  parent: z.string().nullable(),
  /** Local bind TRS. */
  localBind: transformSchema,
  /** World bind derived from the inverse-bind matrix (ADR-003 §2b). */
  worldBind: transformSchema,
});
export type RigBone = z.infer<typeof rigBoneSchema>;

export const rigDescriptorSchema = z.object({
  /** Human-readable rig identity (file name, platform label). */
  label: z.string(),
  /** Ontology version any mapping entries were made against. */
  ontologyVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
  units: z.object({ metersPerUnit: z.number().positive() }),
  upAxis: upAxisSchema,
  facing: facingSchema,
  bones: z.array(rigBoneSchema).min(1),
  /**
   * Mirror pairs whose bind frames were detected as reflection-related —
   * feeds the ADR-005 §7 guard. Empty array = detection ran and found none;
   * this field is REQUIRED so "not checked" can never masquerade as "clean".
   */
  reflectedPairs: z.array(z.tuple([z.string(), z.string()])),
  mapping: z.array(mappingEntrySchema),
});
export type RigDescriptor = z.infer<typeof rigDescriptorSchema>;

/** Validate raw data into a RigDescriptor, loudly (ADR-003 §7). */
export function parseRigDescriptor(raw: unknown): RigDescriptor {
  const parsed = rigDescriptorSchema.safeParse(raw);
  if (!parsed.success) {
    throw new SchemaValidationError(
      'RigDescriptor',
      parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  const rig = parsed.data;

  const boneNames = new Set<string>();
  const structural: string[] = [];
  if (rig.upAxis === 'Z' && (rig.facing === '+Z' || rig.facing === '-Z')) {
    // A facing parallel to up names no world triad — every consumer of the
    // declared convention (canonical-secondary transport, facing/up
    // normalization) would be flying blind (ADR-003 §2b).
    structural.push(`degenerate convention: upAxis Z with facing ${rig.facing}`);
  }
  for (const b of rig.bones) {
    // Names are the rig's identifiers (parent, mapping, reflectedPairs all key
    // on them) — duplicates make every reference ambiguous (Codex review, PR #2).
    if (boneNames.has(b.name)) structural.push(`duplicate rig bone name "${b.name}"`);
    boneNames.add(b.name);
  }
  for (const b of rig.bones) {
    if (b.parent !== null && !boneNames.has(b.parent)) {
      structural.push(`bone "${b.name}" has unknown parent "${b.parent}"`);
    }
  }
  for (const m of rig.mapping) {
    if (!boneNames.has(m.rigBone)) {
      structural.push(`mapping references unknown rig bone "${m.rigBone}"`);
    }
  }
  for (const [a, b] of rig.reflectedPairs) {
    if (!boneNames.has(a) || !boneNames.has(b)) {
      structural.push(`reflectedPairs references unknown bone in [${a}, ${b}]`);
    }
  }
  if (structural.length > 0) throw new SchemaValidationError('RigDescriptor', structural);
  return rig;
}

/**
 * Minor bumps are additive by the ADR-003 §6 semver rule, so a mapping made
 * against an older minor stays valid under a newer one — but never the
 * reverse (the mapping may reference bones the loaded ontology predates),
 * and never across majors.
 */
function versionCompatible(mapping: string, loaded: string): boolean {
  const parse = (v: string): [number, number] => {
    const [maj = 0, min = 0] = v.split('.').map(Number);
    return [maj, min];
  };
  const [mapMajor, mapMinor] = parse(mapping);
  const [loadedMajor, loadedMinor] = parse(loaded);
  return mapMajor === loadedMajor && loadedMinor >= mapMinor;
}

export interface ProfileCoverage {
  profile: string;
  /** Ontology IDs of the profile with no mapping entry. */
  missing: readonly string[];
}

/**
 * Check a rig's mapping against a named ontology profile. Returns coverage;
 * `requireProfileCoverage` is the throwing form for operations that NEED the
 * profile complete (UnmappedBoneError names the first gap; all are reported).
 */
export function profileCoverage(rig: RigDescriptor, ontology: Ontology, profile: string): ProfileCoverage {
  const members = ontology.profiles[profile];
  if (!members) {
    throw new SchemaValidationError('RigDescriptor', [
      `unknown ontology profile "${profile}" (known: ${Object.keys(ontology.profiles).join(', ')})`,
    ]);
  }
  if (!versionCompatible(rig.ontologyVersion, ontology.version)) {
    throw new SchemaValidationError('RigDescriptor', [
      `mapping was made against ontology ${rig.ontologyVersion} but ${ontology.version} is loaded — ` +
        `compatible = same major, loaded minor ≥ mapping minor (ADR-003 §7, §11)`,
    ]);
  }
  const mapped = new Set(rig.mapping.map((m) => m.ontologyId));
  return { profile, missing: members.filter((id) => !mapped.has(id)) };
}

/**
 * Coverage as it WILL BE once the import synthesizes tips (ADR-010 §12) — the
 * only honest question to ask when CHOOSING a profile, as opposed to gating on
 * one already in force.
 *
 * A tip is synthesized from the skin, not read from the rig, and the import only
 * synthesizes tips whose ontology id is in the review queue of the profile it
 * was ASKED for (map-rig walks `profileIds`). So a rig imported at `minimal`
 * never carries the ten `…4` finger tips — and `minimal+hands` requires them.
 * Measured strictly, every rig alive reports those ten missing, so the richer
 * profile was unreachable for any rig, fingered or not: chooseProfile always
 * fell back, and the milestone's finger motion could not arrive on ANY family
 * (measured on a VRoid, app/src/import/profile-choice-probe.test.ts).
 *
 * A tip whose PARENT is mapped is therefore not a gap — it is a bone the import
 * will produce the moment the profile asks for it, which is exactly
 * `synthesizableTipGaps`' rule. A tip whose parent is missing stays a gap: the
 * golden android is missing all four bones of every digit, and forgiving its
 * tips would promise a hand the graft has not built yet.
 *
 * Strictness is not lost, only moved to where it belongs: `requireProfileCoverage`
 * — the gate a retarget actually passes through — still demands the tips EXIST.
 */
export function coverageAfterTipSynthesis(rig: RigDescriptor, ontology: Ontology, profile: string): ProfileCoverage {
  const strict = profileCoverage(rig, ontology, profile);
  const mapped = new Set(rig.mapping.map((m) => m.ontologyId));
  return {
    profile,
    missing: strict.missing.filter((id) => {
      const bone = ontology.byId.get(id);
      return !(bone?.tip === true && bone.parent !== null && mapped.has(bone.parent));
    }),
  };
}

/**
 * The richest profile BOTH rigs can cover, chosen from an ordered preference —
 * richest first — with the reason it stopped where it did.
 *
 * Why this exists rather than a default: profile coverage is REQUIRED
 * (`requireProfileCoverage` throws), so naming a profile a rig cannot cover
 * refuses the whole import. Asking for `minimal+hands` by default would refuse
 * every fingerless generated rig — and the golden android is fingerless
 * (`L_Hand`/`R_Hand` are terminal leaves). Asking for `minimal` always would
 * silently drop the thirty finger channels a Mixamo clip carries onto a rig
 * that HAS fingers. Neither constant is right; the choice is a measurement.
 *
 * A retarget needs ONE profile that both sides satisfy, so this takes every rig
 * that must cover it. The returned `rejected` list is the evidence a surface
 * shows: not "fingers unavailable" but "fingers unavailable BECAUSE this rig is
 * missing LeftHandIndex1, …" — the §8 shape, a refusal that names what it
 * wanted.
 */
export interface ProfileChoice {
  /** The chosen profile — the first in `preference` every rig covered. */
  readonly profile: string;
  /** Richer profiles that were tried and why each failed, richest first. */
  readonly rejected: readonly { readonly profile: string; readonly rig: string; readonly missing: readonly string[] }[];
}

export function chooseProfile(
  rigs: readonly RigDescriptor[],
  ontology: Ontology,
  preference: readonly string[],
): ProfileChoice {
  if (rigs.length === 0) throw new SchemaValidationError('RigDescriptor', ['chooseProfile needs at least one rig']);
  const rejected: Array<{ profile: string; rig: string; missing: readonly string[] }> = [];
  for (const profile of preference) {
    // Coverage AFTER tip synthesis, because the rigs handed here were imported
    // at whatever profile the Document declared — asking the strict question
    // makes every richer profile unreachable (see coverageAfterTipSynthesis).
    // It still throws on an unknown profile name, so a typo in the preference
    // list is a refusal, never a silent skip to the next entry.
    const miss = rigs
      .map((rig) => ({ rig: rig.label, missing: coverageAfterTipSynthesis(rig, ontology, profile).missing }))
      .find((c) => c.missing.length > 0);
    if (miss === undefined) return { profile, rejected };
    rejected.push({ profile, rig: miss.rig, missing: miss.missing });
  }
  const last = preference[preference.length - 1];
  throw new SchemaValidationError('RigDescriptor', [
    `no profile in [${preference.join(', ')}] is covered by every rig — ` +
      rejected.map((r) => `"${r.profile}" missing ${r.missing.length} on "${r.rig}" (${r.missing.slice(0, 4).join(', ')}…)`).join('; ') +
      `. The poorest offered was "${last ?? '(none)'}"; a rig that cannot cover it needs mapping review, not a poorer profile.`,
  ]);
}

export function requireProfileCoverage(
  rig: RigDescriptor,
  ontology: Ontology,
  profile: string,
  operation: string,
): void {
  const coverage = profileCoverage(rig, ontology, profile);
  const first = coverage.missing[0];
  if (first !== undefined) {
    const err = new UnmappedBoneError(first, operation);
    err.message += ` Profile "${profile}" is missing ${coverage.missing.length} bone(s): ${coverage.missing.join(', ')}.`;
    throw err;
  }
}
